import React from 'react';

/**
 * StepReview — Final summary step showing totals for all 14 sections.
 *
 * @param {object} formData - Complete wizard formData keyed by section name.
 */
export default function StepReview({ formData }) {
  // Helper: sum all numeric values in an object (shallow)
  function sumSection(obj) {
    if (!obj) return 0;
    return Object.values(obj).reduce((sum, v) => {
      if (typeof v === 'number') return sum + v;
      if (v && typeof v === 'object') {
        // Nested (auto payments, income sources)
        if (Array.isArray(v)) {
          return sum + v.reduce((s, item) => s + (Number(item?.amount) || 0), 0);
        }
        return sum + (Number(v.amount) || 0);
      }
      return sum;
    }, 0);
  }

  function incomeTotal() {
    const sources = formData.income?.sources || [];
    return sources.reduce((s, src) => s + (Number(src.amount) || 0), 0);
  }

  const sections = [
    { num: 1, label: 'Income', total: incomeTotal() },
    { num: 2, label: 'Housing', total: sumSection(formData.housing) },
    { num: 3, label: 'Bills & Utilities', total: sumSection(formData.bills_utilities) },
    { num: 4, label: 'Auto & Transport', total: sumSection(formData.auto_transport) },
    { num: 5, label: 'Medical', total: sumSection(formData.medical) },
    { num: 6, label: 'Insurance', total: sumSection(formData.insurance) },
    { num: 7, label: 'Entertainment & Travel', total: sumSection(formData.entertainment_travel) },
    { num: 8, label: 'Personal & Family Care', total: sumSection(formData.personal_family_care) },
    { num: 9, label: 'Kids & Education', total: sumSection(formData.kids_education) },
    { num: 10, label: 'Pets', total: sumSection(formData.pets) },
    { num: 11, label: 'Charity', total: sumSection(formData.charity) },
    { num: 12, label: 'Legal / Professional', total: sumSection(formData.legal_professional) },
    { num: 13, label: 'Savings', total: sumSection(formData.savings) },
    { num: 14, label: 'Debts', total: sumSection(formData.debts) },
  ];

  const totalIncome = sections[0].total;
  const totalExpenses = sections
    .filter((s) => ![1, 12].includes(s.num))
    .reduce((sum, s) => sum + s.total, 0);
  const totalSavings = sections[11].total; // section 12
  const surplus = totalIncome - totalExpenses - totalSavings;

  const fmt = (n) =>
    '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

  return (
    <div>
      <div className="mb-4">
        <h2 className="h4 fw-bold">Annual Summary</h2>
        <p className="text-muted">
          Review your totals below. If anything looks off, click the section
          in the sidebar (or use Back) to make corrections.
        </p>
      </div>

      <div className="row g-3 mb-4">
        {sections.map(({ num, label, total }) => (
          <div className="col-12 col-md-6 col-xl-4" key={num}>
            <div className="d-flex justify-content-between align-items-center border rounded p-2 px-3 bg-light">
              <span>
                <span
                  className="badge rounded-pill cashflow-badge-dark me-2"
                  style={{ fontSize: '0.78rem' }}
                >
                  {num}
                </span>
                {label}
              </span>
              <span className="fw-semibold">{total > 0 ? fmt(total) : '—'}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Grand totals */}
      <div className="card bg-light border mb-4">
        <div className="card-body">
          <h5 className="fw-bold mb-3">Annual Summary Calculation</h5>
          <div className="row g-3">
            <div className="col-md-4 text-center">
              <div className="text-muted small">Total Income</div>
              <div className="fs-5 fw-bold text-success">{fmt(totalIncome)}</div>
            </div>
            <div className="col-md-4 text-center">
              <div className="text-muted small">Total Expenses</div>
              <div className="fs-5 fw-bold text-danger">{fmt(totalExpenses)}</div>
            </div>
            <div className="col-md-4 text-center">
              <div className="text-muted small">Total Savings</div>
              <div className="fs-5 fw-bold text-primary">{fmt(totalSavings)}</div>
            </div>
          </div>
          <hr />
          <div className="text-center">
            <div className="text-muted small">
              Surplus or Deficit <span className="text-muted">(Net of Taxes)</span>
            </div>
            <div className={`fs-4 fw-bold ${surplus >= 0 ? 'text-success' : 'text-danger'}`}>
              {fmt(surplus)}
            </div>
          </div>
        </div>
      </div>

      <p className="text-muted small">
        Click <strong>Submit</strong> to send your responses to your advisor.
        You can always come back to review and update them.
      </p>
    </div>
  );
}
